package dev.wyns.ventrova.data.sqlite

import dev.wyns.ventrova.data.OpeningHoursParser
import dev.wyns.ventrova.data.PlaceRepository
import dev.wyns.ventrova.data.model.Address
import dev.wyns.ventrova.data.model.BoundingBox
import dev.wyns.ventrova.data.model.FryOil
import dev.wyns.ventrova.data.model.GeoPoint
import dev.wyns.ventrova.data.model.MenuDiet
import dev.wyns.ventrova.data.model.MenuItem
import dev.wyns.ventrova.data.model.MenuStats
import dev.wyns.ventrova.data.model.Place
import dev.wyns.ventrova.data.model.PlaceCategory
import dev.wyns.ventrova.data.model.VeganLevel
import dev.wyns.ventrova.resources.Res
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

/**
 * Reads places from the SQLite snapshot bundled in the app resources (see
 * `scripts/sync-database.sh`). The snapshot is small, so all places are loaded once and queried in
 * memory; menus are read on demand from the database, which stays open.
 */
class SqlitePlaceRepository(
    private val opener: SqlDatabaseOpener,
) : PlaceRepository {

    private val mutex = Mutex()
    private var places: List<Place>? = null
    private var database: SqlDatabase? = null

    override suspend fun placesIn(bounds: BoundingBox): List<Place> = all().filter { it.location in bounds }

    override suspend fun search(query: String, limit: Int): List<Place> {
        val needle = query.normalizedForSearch()
        if (needle.isEmpty()) return emptyList()
        return all()
            .filter { place ->
                listOf(place.name, place.address.city.orEmpty(), place.cuisines.joinToString(" "), place.searchKeywords())
                    .any { it.normalizedForSearch().contains(needle) }
            }
            .sortedBy { if (it.name.normalizedForSearch().startsWith(needle)) 0 else 1 }
            .take(limit)
    }

    override suspend fun placesById(ids: Collection<String>): List<Place> {
        val wanted = ids.toSet()
        return all().filter { it.id in wanted }
    }

    override suspend fun menuFor(placeId: String): List<MenuItem> {
        all()
        val db = database ?: return emptyList()
        return db.query(
            """
            SELECT name, description, section, course, price, diet, diet_labeled, diet_confidence
            FROM place_menu_items
            WHERE place_id = ?
            ORDER BY rowid
            """.trimIndent(),
            placeId,
        ).mapNotNull { row ->
            MenuItem(
                name = row.string("name") ?: return@mapNotNull null,
                description = row.string("description"),
                section = row.string("section"),
                course = row.string("course") ?: "unknown",
                price = row.double("price"),
                diet = MenuDiet.fromDatabase(row.string("diet")),
                labeled = row.long("diet_labeled") == 1L,
                confidence = row.double("diet_confidence"),
            )
        }
    }

    private suspend fun all(): List<Place> = mutex.withLock {
        places ?: load().also { places = it }
    }

    private suspend fun load(): List<Place> {
        val db = opener.open(DATABASE_NAME, Res.readBytes("files/$DATABASE_NAME")).also { database = it }
        val openingHours = db.query("SELECT place_id, opening_hours FROM place_opening_hours")
            .associate { it.string("place_id") to it.string("opening_hours") }
        val menus = db.query("SELECT place_id, food_items, vegan_items, vegetarian_items FROM place_menu_summary")
            .associate { row ->
                row.string("place_id") to MenuStats(
                    foodItems = row.long("food_items")?.toInt() ?: 0,
                    veganItems = row.long("vegan_items")?.toInt() ?: 0,
                    vegetarianItems = row.long("vegetarian_items")?.toInt() ?: 0,
                )
            }

        return db.query(
            """
            SELECT id, name, lat, lng, category, cuisine, street, housenumber, postcode, city,
                   website, phone, email, veg_status, fry_oil, source_categories
            FROM places
            WHERE status = 'active'
            """.trimIndent(),
        ).mapNotNull { row ->
            val id = row.string("id") ?: return@mapNotNull null
            val name = row.string("name") ?: return@mapNotNull null
            val category = PlaceCategory.fromDatabase(row.string("category").orEmpty()) ?: return@mapNotNull null
            val cuisines = row.string("cuisine")?.split(',', ';')?.map { it.trim() }?.filter { it.isNotEmpty() }.orEmpty()
            Place(
                id = id,
                name = name,
                category = if (isChipShop(name, category, cuisines, row.string("source_categories"))) PlaceCategory.ChipShop else category,
                veganLevel = VeganLevel.fromDatabase(row.string("veg_status").orEmpty()) ?: return@mapNotNull null,
                location = GeoPoint(
                    latitude = row.double("lat") ?: return@mapNotNull null,
                    longitude = row.double("lng") ?: return@mapNotNull null,
                ),
                cuisines = cuisines,
                address = Address(
                    street = row.string("street"),
                    houseNumber = row.string("housenumber"),
                    postcode = row.string("postcode"),
                    city = row.string("city"),
                ),
                website = row.string("website"),
                phone = row.string("phone"),
                email = row.string("email"),
                openingHours = OpeningHoursParser.parse(openingHours[id]),
                fryOil = row.string("fry_oil")?.let(FryOil::fromDatabase),
                menu = menus[id],
            )
        }
    }

    private companion object {
        const val DATABASE_NAME = "veganmaps.sqlite"
    }
}

/**
 * The database has no chip shop category (frituren are `fast_food`), so it is derived: by name, or
 * by the `friture` cuisine (OSM) or `friterie` category (Overture). Restaurants only count when
 * `friture` is their main cuisine, so a brasserie that also serves fries stays a restaurant.
 */
internal fun isChipShop(name: String, category: PlaceCategory, cuisines: List<String>, sourceCategories: String?): Boolean {
    if (ChipShopName.containsMatchIn(name)) return true
    return when (category) {
        PlaceCategory.FastFood, PlaceCategory.FoodTruck ->
            "friture" in cuisines || sourceCategories?.contains("\"friterie\"") == true
        PlaceCategory.Restaurant -> cuisines.firstOrNull() == "friture"
        else -> false
    }
}

private val ChipShopName = Regex("""\b(frituur|friterie|frietkot|fritkot|frietkraam)""", RegexOption.IGNORE_CASE)

/** Lets "friet" or "fries" find chip shops whose name doesn't say so. */
private fun Place.searchKeywords(): String =
    if (category == PlaceCategory.ChipShop) "frituur friterie frietkot friet frieten frietjes frites fries chips" else ""

private val diacritics = mapOf(
    'à' to 'a', 'á' to 'a', 'â' to 'a', 'ä' to 'a', 'ç' to 'c', 'è' to 'e', 'é' to 'e', 'ê' to 'e',
    'ë' to 'e', 'ì' to 'i', 'í' to 'i', 'î' to 'i', 'ï' to 'i', 'ñ' to 'n', 'ò' to 'o', 'ó' to 'o',
    'ô' to 'o', 'ö' to 'o', 'ù' to 'u', 'ú' to 'u', 'û' to 'u', 'ü' to 'u', 'ÿ' to 'y',
)

internal fun String.normalizedForSearch(): String =
    trim().lowercase().map { diacritics[it] ?: it }.joinToString("")
